/**
 * Tiny typed HTTP client.
 *
 * Sends `credentials: 'include'` on every request so the signed session cookie
 * (set by the backend after login) is passed back. Same-origin in production;
 * dev-mode requests are proxied by Vite (see vite.config.ts).
 *
 * On non-2xx responses, throws ApiError with the parsed body when possible.
 *
 * Two flavours of public methods:
 *   - `api.get / .post / .patch / .delete` return the parsed body (T).
 *   - `api.getMeta / .postMeta / .patchMeta / .deleteMeta` return the body
 *     plus { status, headers } so callers can react to 202 Accepted (Phase
 *     B2 maker-checker pending writes) and other status-code-bearing flows.
 */
export class ApiError extends Error {
  status: number;
  detail: string;
  code: string | null;
  // Task #155 (2026-06-02) — full parsed response body, preserved so
  // downstream consumers can access STRUCTURED error payloads that
  // don't fit the plain-string `detail` shape (e.g., the password-policy
  // 422 response carries detail = { code, errors: [{code, message}] }).
  // `null` when the response body wasn't JSON.
  body: unknown;
  constructor(status: number, detail: string, code: string | null = null, body: unknown = null) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.code = code;
    this.body = body;
  }
}

export interface ApiMeta<T> {
  data: T;
  status: number;
  headers: Headers;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

// Task #256 v3 (2026-06-04): DEV-only fetch-level retry to absorb the
// uvicorn --reload window. Two prior attempts to fix this in vite.config.ts
// failed (timeout settings don't apply to connect-phase errors; on('error')
// retries race with http-proxy's default response writer). Solving it
// here is cleaner: we see both 5xx HTTP responses AND network errors,
// and we can decide retry policy without fighting http-proxy internals.
//
// Behaviour:
//   - DEV: up to 3 attempts total, with 500ms / 1000ms / 2000ms backoff
//     between them. Retry triggers on either fetch() throwing (network
//     error / Vite proxy gave up) or on a 5xx response (Vite's transient
//     proxy-error response while uvicorn is restarting).
//   - PRODUCTION: 1 attempt, no retry. Real backend 5xx errors must not
//     be masked, and POST/PATCH/DELETE non-idempotent operations must
//     not be silently double-executed.
//
// Safety on non-idempotent verbs in DEV: a Vite proxy-induced 5xx means
// the request never reached uvicorn, so no double-execution risk. A real
// backend 5xx would, but the DEV gate accepts that minor risk for the
// significantly cleaner dev experience.
const RETRY_STATUSES = new Set([500, 502, 503, 504]);
const RETRY_BACKOFF_MS = [500, 1000, 2000];

async function fetchWithRetry(path: string, init: RequestInit): Promise<Response> {
  const maxAttempts = import.meta.env.DEV ? 3 : 1;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await fetch(path, init);
      if (RETRY_STATUSES.has(res.status) && attempt < maxAttempts - 1) {
        await new Promise<void>((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt] ?? 2000));
        continue;
      }
      return res;
    } catch (networkErr) {
      if (attempt < maxAttempts - 1) {
        await new Promise<void>((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt] ?? 2000));
        continue;
      }
      throw networkErr;
    }
  }
  // Unreachable — the loop above either returns or throws on the last
  // attempt. TypeScript needs the throw for control-flow analysis.
  throw new Error('fetchWithRetry: exhausted attempts without returning');
}

async function _do<T>(method: Method, path: string, body?: unknown): Promise<ApiMeta<T>> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const res = await fetchWithRetry(path, init);

  // 204 No Content
  if (res.status === 204) {
    return { data: undefined as T, status: res.status, headers: res.headers };
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    // Plain string detail is the common case. When detail is an object
    // (FastAPI passes structured payloads through verbatim), try a
    // nested `.message` first so the human-readable string still shows
    // up in toast/alert callers that haven't been taught to look at
    // `body`. Falls back to a friendly status-aware default when neither
    // shape applies — see friendlyFallback() below (Task #252).
    let detail = friendlyFallback(res.status);
    if (data && typeof data === 'object' && 'detail' in data) {
      const d = (data as { detail: unknown }).detail;
      if (typeof d === 'string') {
        detail = d;
      } else if (
        d && typeof d === 'object' && 'message' in d
        && typeof (d as { message: unknown }).message === 'string'
      ) {
        detail = (d as { message: string }).message;
      }
    }
    const code =
      (data && typeof data === 'object' && 'code' in data && typeof data.code === 'string')
        ? data.code
        : null;
    throw new ApiError(res.status, detail, code, data);
  }

  return { data: data as T, status: res.status, headers: res.headers };
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const r = await _do<T>(method, path, body);
  return r.data;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Task #252 (2026-06-04): friendlier default detail when the response
// body didn't yield a usable error message. The pre-#192 world hid this
// behind the modal title ("Could not update user — HTTP 500"); after the
// Tier-2 migration, the bare "HTTP 500" surfaces in the inline banner
// with no context. This helper picks a message that's actionable for
// the user without losing the technical status code.
//
// Detail rules:
//   - 5xx: server error — actionable retry guidance + escalation note.
//   - 408 / 504: timeouts — explicit "took too long" wording.
//   - 503: service unavailable — explicit "temporarily unavailable".
//   - 401 / 403 / 404: backend almost always sends a detail string, so
//     this fallback is rarely reached for those; if it IS reached, keep
//     it short and factual.
//   - Otherwise: original "HTTP <status>" fallback so nothing is lost.
function friendlyFallback(status: number): string {
  if (status === 503) {
    return 'Service temporarily unavailable. Try again in a moment.';
  }
  if (status === 408 || status === 504) {
    return 'The request took too long. Check your connection and try again.';
  }
  if (status >= 500) {
    return `Server error (HTTP ${status}). Try again, and if it persists contact support.`;
  }
  if (status === 401) {
    return 'Not signed in. Refresh the page and sign in again.';
  }
  if (status === 403) {
    return `You don't have permission to do that.`;
  }
  if (status === 404) {
    return 'Not found.';
  }
  return `HTTP ${status}`;
}

export const api = {
  // Body-only convenience methods (the common case).
  get:    <T>(path: string)                        => request<T>('GET',    path),
  post:   <T>(path: string, body?: unknown)        => request<T>('POST',   path, body),
  patch:  <T>(path: string, body?: unknown)        => request<T>('PATCH',  path, body),
  put:    <T>(path: string, body?: unknown)        => request<T>('PUT',    path, body),
  delete: <T>(path: string)                        => request<T>('DELETE', path),
  // Meta-aware variants — return body + status + headers. Use these when the
  // caller needs to inspect status (e.g., 202 vs 200) or response headers
  // (e.g., X-Change-Request-Id from the maker-checker pipeline).
  getMeta:    <T>(path: string)                    => _do<T>('GET',    path),
  postMeta:   <T>(path: string, body?: unknown)    => _do<T>('POST',   path, body),
  patchMeta:  <T>(path: string, body?: unknown)    => _do<T>('PATCH',  path, body),
  putMeta:    <T>(path: string, body?: unknown)    => _do<T>('PUT',    path, body),
  deleteMeta: <T>(path: string)                    => _do<T>('DELETE', path),
};
